import { useCallback, useMemo } from "react";
import { useMutation, useQuery, type ApolloCache } from "@apollo/client";
import {
  RECENTLY_ASSUMED_USERS_FIRST,
  RECENTLY_ASSUMED_USERS_QUERY,
  type RecentlyAssumedUserRow
} from "@/pages/assume-user/queries";
import { RECORD_RECENTLY_ASSUMED_USER_MUTATION } from "@/pages/assume-user/mutations";

import type { RecordRecentlyAssumedUserResponse } from "@/pages/assume-user/mutations";

export type RecentlyAssumedUser = {
  id: string;
  name: string;
  email: string | null;
  photo?: string | null;
  department?: string | null;
  assumedAt: string;
};

export type UseRecentlyAssumedUsersOptions = {
  skip?: boolean;
};

function rowToUser(row: RecentlyAssumedUserRow): RecentlyAssumedUser {
  return {
    id: row.user.id,
    name: row.user.name,
    email: row.user.email,
    photo: row.user.photo,
    department: row.user.department,
    assumedAt: row.assumedAt
  };
}

function mergeRecordedRow(
  cache: ApolloCache<unknown>,
  payload: RecordRecentlyAssumedUserResponse["recordRecentlyAssumedUser"] | undefined
): void {
  if (!payload?.success) return;
  const row = payload.recentlyAssumedUsers[0];
  if (!row) return;
  const cached = cache.readQuery({
    query: RECENTLY_ASSUMED_USERS_QUERY,
    variables: { first: RECENTLY_ASSUMED_USERS_FIRST }
  }) as { recentlyAssumedUsers: RecentlyAssumedUserRow[] } | null;
  // readQuery returns null when the list query was skipped or never ran;
  // the next fetch repopulates it, so start from an empty list.
  const previous = cached?.recentlyAssumedUsers ?? [];
  cache.writeQuery({
    query: RECENTLY_ASSUMED_USERS_QUERY,
    variables: { first: RECENTLY_ASSUMED_USERS_FIRST },
    data: {
      recentlyAssumedUsers: [row, ...previous.filter((entry) => entry.user.id !== row.user.id)].slice(
        0,
        RECENTLY_ASSUMED_USERS_FIRST
      )
    }
  });
}

export function useRecentlyAssumedUsers(options: UseRecentlyAssumedUsersOptions = {}) {
  const { data, loading, error } = useQuery(RECENTLY_ASSUMED_USERS_QUERY, {
    variables: { first: RECENTLY_ASSUMED_USERS_FIRST },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    skip: options.skip === true
  });
  const [recordMutation] = useMutation(RECORD_RECENTLY_ASSUMED_USER_MUTATION, {
    update(cache, { data: mutationData }) {
      mergeRecordedRow(cache, mutationData?.recordRecentlyAssumedUser);
    }
  });

  const recentlyAssumedUsers = useMemo(
    () => (data?.recentlyAssumedUsers ?? []).map(rowToUser),
    [data?.recentlyAssumedUsers]
  );

  const recordRecentlyAssumed = useCallback(
    (assumedUserId: string) => {
      const targetId = assumedUserId.trim();
      if (!targetId) return Promise.resolve();
      // Best-effort: recording must never block the assume flow.
      return recordMutation({ variables: { assumedUserId: targetId } }).then(
        () => undefined,
        () => undefined
      );
    },
    [recordMutation]
  );

  return { recentlyAssumedUsers, loading, error, recordRecentlyAssumed };
}
